import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import {useLinkResolver} from 'prismicio'
import {Fragment, useCallback, useState} from 'react'
import {Link} from 'src/common/Link'
import NextLink from 'next/link'
import {PrismicNextImage} from '@prismicio/next'
import {CookieConsentPopup} from 'src/common/Layout/Footer/CookieConsentPopup'
import {NavigationDocumentData} from 'prismicio-types'
import {Icon} from 'src/common/Icon'

/**
 * Props for `FooterNoNav`.
 */
export type FooterNoNavV2Props = {
  navigationData: NavigationDocumentData
}

/**
 * Component for "FooterNoNav" Slices.
 */
export const FooterNoNavV2 = ({navigationData}: FooterNoNavV2Props): JSX.Element => {
  const [isPopupShown, setIsPopupShown] = useState(false)
  const close = useCallback(() => setIsPopupShown(false), [])
  const {linkResolver} = useLinkResolver()

  const showCookieConsent = useCallback(() => {
    if (typeof window !== 'undefined') {
      // @ts-ignore -- usercentrics script is loaded by GTM
      window.UC_UI?.showSecondLayer()
    }
  }, [])

  return (
    <footer className="bg-green-dark pb-12 pt-20 text-ffffff">
      <div className="mx-auto flex max-w-[1200px] flex-col px-5 max-md:gap-12 md:flex-row md:gap-20 md:max-lg:px-10 xl:px-0">
        <Icon name="moss-woodmark" className="h-[43px] w-[192px] text-moss-white" />

        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-6 gap-y-3">
            {navigationData.underline_links.map((link, index) => (
              <Fragment key={index}>
                <Link
                  className="text-tg-body-s font-medium leading-[normal] text-moss-white/[70%]"
                  href={linkResolver(link.link)}
                  {...(isFilled.link(link.link) && link.link.link_type === 'Web' && {target: link.link.target})}
                  isInverted
                >
                  {link.label}
                </Link>
              </Fragment>
            ))}

            <button
              className="text-tg-body-s font-medium leading-[normal] text-moss-white/[70%]"
              onClick={showCookieConsent}
              type="button"
            >
              {navigationData.cookie_consent_label}
            </button>
          </div>

          <div className="text-tg-body-xs max-w-[385px] text-moss-white/[70%]">
            <PrismicRichText field={navigationData.description} />
          </div>

          <div className="text-tg-body-xs flex gap-6 text-green-neon max-md:pt-6">
            <span>{navigationData.copyright}</span>
            <span>{navigationData.additional_text}</span>
          </div>
        </div>
      </div>

      {isFilled.image(navigationData.image) && (
        <NextLink
          aria-label={navigationData.image.alt || undefined}
          className="col-start-[track-start_5] col-end-[track-end_6] row-start-5 mt-7.5 justify-self-end md:col-start-[track-start_12] md:mt-0 md:self-end"
          href={linkResolver(navigationData.image_link)}
          {...(isFilled.link(navigationData.image_link) &&
            navigationData.image_link.link_type === 'Web' && {target: navigationData.image_link.target})}
        >
          <PrismicNextImage field={navigationData.image} />
        </NextLink>
      )}

      <CookieConsentPopup isShown={isPopupShown} navigationData={navigationData} close={close} />
    </footer>
  )
}
